import React, { useEffect, useRef, useCallback, useState } from 'react';

const MASK_CHAR = '*';
const DEFAULT_MASK_DELAY_MS = 400;

const digitsOnly = (raw, maxLength) =>
	String(raw ?? '')
		.replace(/\D/g, '')
		.slice(0, maxLength);

/**
 * Numeric secret input: each digit stays visible briefly, then becomes *.
 */
const DelayedMaskInput = ({
	value = '',
	onChange,
	maxLength = 8,
	maskDelayMs = DEFAULT_MASK_DELAY_MS,
	onEnter,
	inputRef,
	...inputProps
}) => {
	const revealedUntilRef = useRef({});
	const timeoutRefs = useRef({});
	const valueRef = useRef(value);
	const [, setTick] = useState(0);
	valueRef.current = value;

	const forceUpdate = useCallback(() => {
		setTick((t) => t + 1);
	}, []);

	const clearIndexTimer = (index) => {
		if (timeoutRefs.current[index]) {
			clearTimeout(timeoutRefs.current[index]);
			delete timeoutRefs.current[index];
		}
		delete revealedUntilRef.current[index];
	};

	const clearAllTimers = () => {
		Object.keys(timeoutRefs.current).forEach((key) => {
			clearTimeout(timeoutRefs.current[key]);
		});
		timeoutRefs.current = {};
		revealedUntilRef.current = {};
	};

	const scheduleMask = (index) => {
		clearIndexTimer(index);
		revealedUntilRef.current[index] = Date.now() + maskDelayMs;
		timeoutRefs.current[index] = setTimeout(() => {
			clearIndexTimer(index);
			forceUpdate();
		}, maskDelayMs);
	};

	const revealRange = (fromIndex, toIndex) => {
		for (let i = fromIndex; i < toIndex; i += 1) {
			scheduleMask(i);
		}
	};

	const getDisplayValue = useCallback(() => {
		const now = Date.now();
		return value
			.split('')
			.map((char, index) => {
				const until = revealedUntilRef.current[index];
				if (until && until > now) return char;
				return MASK_CHAR;
			})
			.join('');
	}, [value]);

	const applyValue = (nextRaw) => {
		const prev = valueRef.current;
		const next = digitsOnly(nextRaw, maxLength);
		if (next === prev) return;

		if (next.length > prev.length) {
			revealRange(prev.length, next.length);
		} else if (next.length < prev.length) {
			for (let i = next.length; i < prev.length; i += 1) {
				clearIndexTimer(i);
			}
		} else {
			for (let i = 0; i < next.length; i += 1) {
				if (next[i] !== prev[i]) scheduleMask(i);
			}
		}

		onChange(next);
		forceUpdate();
	};

	useEffect(
		() => () => {
			clearAllTimers();
		},
		[]
	);

	useEffect(() => {
		if (value === '') {
			clearAllTimers();
			forceUpdate();
		}
	}, [value, forceUpdate]);

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			onEnter?.(e);
			return;
		}
		if (e.key === 'Backspace') {
			e.preventDefault();
			applyValue(value.slice(0, -1));
		}
	};

	const handleChange = (e) => {
		const raw = e.target.value;
		const prevDisplay = getDisplayValue();

		if (raw.length > prevDisplay.length) {
			const appended = digitsOnly(raw.slice(prevDisplay.length), maxLength);
			if (appended) {
				applyValue(value + appended);
				return;
			}
		}

		if (raw.length < prevDisplay.length) {
			applyValue(value.slice(0, value.length - (prevDisplay.length - raw.length)));
			return;
		}

		const onlyDigits = digitsOnly(raw, maxLength);
		if (onlyDigits !== value) {
			applyValue(onlyDigits);
		}
	};

	const handlePaste = (e) => {
		e.preventDefault();
		const pasted = digitsOnly(e.clipboardData.getData('text'), maxLength);
		if (!pasted) return;
		applyValue((value + pasted).slice(0, maxLength));
	};

	return (
		<input
			{...inputProps}
			ref={inputRef}
			type="text"
			inputMode="numeric"
			autoComplete={inputProps.autoComplete ?? 'one-time-code'}
			value={getDisplayValue()}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			onPaste={handlePaste}
		/>
	);
};

export default DelayedMaskInput;
